import config from "../config/config";

// Webメルカトル基準でズーム0時のメートル/ピクセル
// map.transform.tileSizeが512（実測）なので、zoom0のmeters/px は「地球円周 / 512」
const EARTH_RADIUS_M = 6378137;
const EARTH_CIRCUMFERENCE_M = 2 * Math.PI * EARTH_RADIUS_M;
const TILE_SIZE_ZOOM0_FALLBACK = config.map.tileSize;

// 角度変換定数（度 -> ラジアン）
const DEG_TO_RAD = Math.PI / 180;

const HUNDRED_METERS = 100;
const TEN_METERS = 10;
const DEFAULT_MAX_WIDTH = 100;
const METERS_PER_KM = Number(config.common.conversions.metersPerKm);
const UNIT_METER = config.common.unit.meter;
const UNIT_KILOMETER = config.common.unit.kilometer;

/**
 * スケールバーの計算を行う関数
 * @param {Object} map - マップインスタンス
 * @param {number} maxWidth - スケールバーの最大幅
 * @returns {Object} スケールバーの情報
 */
export function calculateScale(map, maxWidth = DEFAULT_MAX_WIDTH) {
  // 地図の中心
  const center = map.getCenter();

  // メートル/ピクセルの計算
  // 理論: metersPerPixelAtZoom0 = 地球円周 / tileSize(zoom0)
  // metersPerPixel = metersPerPixelAtZoom0 * cos(lat) / 2^zoom
  const tileSizeZoom0 = map?.transform?.tileSize ?? TILE_SIZE_ZOOM0_FALLBACK;
  const metersPerPixelAtZoom0 = EARTH_CIRCUMFERENCE_M / tileSizeZoom0;

  const metersPerPixel =
    metersPerPixelAtZoom0 *
    Math.cos(center.lat * DEG_TO_RAD) /
    Math.pow(2, map.getZoom());

  // スケールバーの距離計算
  const distance = metersPerPixel * maxWidth;

  // 距離を適切な単位に変換
  let roundedDistance, unit;
  if (distance >= METERS_PER_KM) {
    // 1km以上の場合はkm変換
    roundedDistance = Math.round(distance / METERS_PER_KM);
    unit = UNIT_KILOMETER;
    // 100m以上の場合は100m以上は100m単位で丸める
  } else if (distance >= HUNDRED_METERS) {
    roundedDistance = Math.round(distance / HUNDRED_METERS) * HUNDRED_METERS;
    unit = UNIT_METER;
    // 10m以上の場合は10m単位で丸める
  } else if (distance >= TEN_METERS) {
    roundedDistance = Math.round(distance / TEN_METERS) * TEN_METERS;
    unit = UNIT_METER;
  } else {
    // 10m未満の場合は1m単位
    roundedDistance = Math.round(distance);
    unit = UNIT_METER;
  }

  // スケールバーの表示幅
  // 1) 「スケールバーに表示する値」をメートルに揃える
  // 例: 表示が"1km"の場合は1000（m）にする
  let displayDistanceMeters;
  if (unit === UNIT_KILOMETER) {
    displayDistanceMeters = roundedDistance * METERS_PER_KM;
  } else {
    displayDistanceMeters = roundedDistance;
  }

  // 2) maxWidthピクセルが表す距離(distance)に対して、
  // 表示距離(displayDistanceMeters)が何割かを求める（割合 = 表示距離 ÷ distance）
  // 例: distance = 750m, displayDistanceMeters = 500m → ratio = 0.666...
  let widthRatio = 0;
  if (Number.isFinite(distance) && distance > 0) {
    widthRatio = displayDistanceMeters / distance;
  }

  // 3) 比率に maxWidth を掛けて実際に描画するピクセル幅を算出
  const width = widthRatio * maxWidth;

  return {
    distance: roundedDistance, // スケールバーに表示する数値（単位は unit）
    unit, // 単位（'km' または 'm'）
    width // スケールバーの表示幅（ピクセル）
  };
}